/**
 * Переиспользуемый компонент выпадающего фильтра
 * Дата: 30.01.2026
 * 
 * Используется в:
 * - Users.tsx — фильтр по роли, статусу
 * - Families.tsx — фильтр по статусу
 * - Services.tsx — фильтр по категории, активности
 * - Templates.tsx — фильтр по типу, каналу
 * - Messages.tsx — фильтр по каналу, направлению
 * - AutoReplies.tsx — фильтр по типу триггера, статусу
 * - PhoneBinding.tsx — фильтр по статусу привязки
 * 
 * Связан с: components/ui/index.ts (экспорт)
 */
import { cn } from '@/utils/cn';
import { ChevronDown } from 'lucide-react';

export interface FilterOption {
  /** Значение опции */
  value: string;
  /** Отображаемый текст */
  label: string;
  /** Иконка (опционально) */
  icon?: React.ReactNode;
  /** Disabled состояние */
  disabled?: boolean;
}

export interface FilterSelectProps {
  /** Текущее выбранное значение */
  value: string;
  /** Callback при изменении значения */
  onChange: (value: string) => void;
  /** Список опций */
  options: FilterOption[];
  /** Placeholder для пустого значения */
  placeholder?: string;
  /** Дополнительные CSS классы */
  className?: string;
  /** Размер: sm, md, lg */
  size?: 'sm' | 'md' | 'lg';
  /** Disabled состояние */
  disabled?: boolean;
  /** Label для accessibility */
  'aria-label'?: string;
  /** data-testid для тестов */
  testId?: string;
  /** Показывать "Все" опцию */
  showAllOption?: boolean;
  /** Текст для "Все" опции */
  allOptionLabel?: string;
}

/**
 * FilterSelect — унифицированный компонент выпадающего фильтра
 * 
 * @example
 * // Базовое использование
 * <FilterSelect
 *   value={filter}
 *   onChange={setFilter}
 *   options={[
 *     { value: 'active', label: 'Активные' },
 *     { value: 'inactive', label: 'Неактивные' },
 *   ]}
 * />
 * 
 * @example
 * // С опцией "Все" и кастомным размером
 * <FilterSelect
 *   value={roleFilter}
 *   onChange={setRoleFilter}
 *   options={roles}
 *   showAllOption
 *   allOptionLabel="Все роли"
 *   size="lg"
 * />
 */
export function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
  size = 'md',
  disabled = false,
  'aria-label': ariaLabel,
  testId,
  showAllOption = true,
  allOptionLabel = 'Все',
}: FilterSelectProps) {
  // Размеры
  const sizeClasses = {
    sm: 'h-8 text-sm px-3 pr-8',
    md: 'h-10 text-base px-4 pr-10',
    lg: 'h-12 text-lg px-4 pr-12',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const iconPosition = {
    sm: 'right-2',
    md: 'right-3',
    lg: 'right-3',
  };

  return (
    <div className={cn('relative inline-block', className)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
        className={cn(
          'shell-select appearance-none cursor-pointer',
          sizeClasses[size]
        )}
      >
        {/* Опция "Все" */}
        {showAllOption && (
          <option value="">{placeholder || allOptionLabel}</option>
        )}
        
        {/* Основные опции */}
        {options.map((option) => (
          <option 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      
      {/* Иконка стрелки */}
      <ChevronDown 
        className={cn(
          'absolute top-1/2 -translate-y-1/2 text-shell-muted pointer-events-none',
          iconSizeClasses[size],
          iconPosition[size]
        )} 
      />
    </div>
  );
}

/**
 * Пресеты для часто используемых фильтров
 */
export const filterPresets = {
  /** Фильтр активности */
  activeStatus: [
    { value: 'active', label: 'Активные' },
    { value: 'inactive', label: 'Неактивные' },
  ] as FilterOption[],
  
  /** Каналы сообщений */
  messageChannels: [
    { value: 'telegram_bot', label: 'Telegram Bot' },
    { value: 'telegram_client', label: 'Telegram Client' },
    { value: 'whatsapp_regular', label: 'WhatsApp Regular' },
    { value: 'whatsapp_waba', label: 'WhatsApp WABA' },
    { value: 'max', label: 'MAX' },
    { value: 'max_client', label: 'MAX Client' },
    { value: 'sms', label: 'SMS' },
    { value: 'sms_tel', label: 'SMS-Tel' },
  ] as FilterOption[],
  
  /** Типы триггеров автоответов */
  autoReplyTriggers: [
    { value: 'feedback', label: 'Обратная связь' },
    { value: 'first_message', label: 'Первое сообщение' },
    { value: 'keyword', label: 'Ключевое слово' },
    { value: 'regex', label: 'Регулярное выражение' },
    { value: 'waba_button', label: 'Кнопка WABA шаблона' },
    { value: 'tg_callback', label: 'Inline callback' },
    { value: 'inbound_first_contact', label: '📩 Первичное обращение' }, // TODO-061
  ] as FilterOption[],
};
